import React from "react";
import Section from "./Section";

export default function HTML_Links_Bookmarks() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🔖 HTML Links — Bookmarks</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        HTML <strong>bookmarks</strong> allow you to jump to a specific part of
        a webpage. You can create bookmarks using the{" "}
        <code className="highlight_code_1">id</code> attribute and link to them
        with <code className="highlight_code_1">href="#id"</code>.
      </p>

      {/* 🔹 Info Box */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#22c55e",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          🧭 Bookmark Basics
        </h3>
        <ul className="tutorial-list-points">
          <li>
            Add an <code className="highlight_code_1">id</code> to the element
            you want to link to.
          </li>
          <li>
            Create a link with{" "}
            <code className="highlight_code_1">href="#id"</code> to jump to that
            section.
          </li>
          <li>Works within the same page or even across pages.</li>
        </ul>
      </div>

      {/* Section 1️⃣ — Simple Bookmark Example */}
      <Section
        title="1️⃣ Simple Bookmark Example"
        html={`<h2 id="section1">This is Section 1</h2>
<p><a href="#section1">Go to Section 1</a></p>`}
        description="This example creates a link that jumps to a specific section (marked with id='section1') on the same page."
      />

      {/* Section 2️⃣ — Multiple Bookmarks on a Page */}
      <Section
        title="2️⃣ Multiple Bookmarks Example"
        html={`<h2 id="intro">Introduction</h2>
<p>Welcome to our HTML tutorial!</p>

<p><a href="#chapter1">Go to Chapter 1</a> | <a href="#chapter2">Go to Chapter 2</a></p>

<h3 id="chapter1">Chapter 1</h3>
<p>This is Chapter 1 content...</p>

<h3 id="chapter2">Chapter 2</h3>
<p>This is Chapter 2 content...</p>`}
        description="You can create multiple bookmarks on the same page using different ids and links pointing to them."
      />

      {/* Section 3️⃣ — Bookmark Linking Across Pages */}
      <Section
        title="3️⃣ Linking to a Bookmark on Another Page"
        html={`<p><a href="page2.html#contact">Go to Contact Section on Page 2</a></p>`}
        description="You can also link to a specific section on another page using the page name followed by the bookmark id — for example, 'page2.html#contact'."
      />

      {/* Section 4️⃣ — Styling Bookmark Links */}
      <Section
        title="4️⃣ Styling Bookmark Links"
        html={`<style>
a { color: #007bff; text-decoration: none; }
a:hover { text-decoration: underline; color: #ff5722; }
</style>

<p><a href="#tips">Jump to Tips Section</a></p>

<h3 id="tips">📘 Tips Section</h3>
<p>Use clear and descriptive bookmark names like #contact, #about, or #faq.</p>`}
        description="You can use CSS to make your bookmarks and navigation links more attractive and user-friendly."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — HTML Bookmarks</h2>
        <ul className="tutorial-list-points">
          <li>Bookmarks help you jump to specific parts of a page.</li>
          <li>
            Use the <code className="highlight_code_1">id</code> attribute to
            mark a section.
          </li>
          <li>
            Use <code className="highlight_code_1">&lt;a href="#id"&gt;</code>{" "}
            to link to that section.
          </li>
          <li>
            You can also link to bookmarks on other pages:
            <br />
            <code className="highlight_code_1">page.html#section</code>
          </li>
          <li>
            Styling with CSS can make bookmark links more visually appealing.
          </li>
        </ul>
      </div>
    </div>
  );
}
